import type { AuditBase, Page } from '../http';
import type { DocumentVersionIdentifier } from './common';
import type { ContentCreated, ContentDeleted, ContentOCRRequested, ContentOCRViewed, ContentUploaded } from './contents';
import type { DocumentVersion } from './documents';

export enum DocumentEditionEventBase {
  CONTENT_CREATED = 'CONTENT_CREATED',
  CONTENT_DELETED = 'CONTENT_DELETED',
  CONTENT_OCR_DELETED = 'CONTENT_OCR_DELETED',
  CONTENT_OCR_REQUESTED = 'CONTENT_OCR_REQUESTED',
  CONTENT_UPLOADED = 'CONTENT_UPLOADED',
  DOCUMENT_CREATED = 'DOCUMENT_CREATED',
  DOCUMENT_TREE_DELETED = 'DOCUMENT_TREE_DELETED',
  DOCUMENT_VERSIONED = 'DOCUMENT_VERSIONED',
}

export enum DocumentViewingEventBase {
  CONTENT_OCR_VIEWED = 'CONTENT_OCR_VIEWED',
  CONTENT_VIEWED = 'CONTENT_VIEWED',
  CONTENTS_LISTED = 'CONTENTS_LISTED',
  DOCUMENT_RETRIEVED = 'DOCUMENT_RETRIEVED',
  DOCUMENT_VERSIONS_LISTED = 'DOCUMENT_VERSIONS_LISTED',
}

export const DocumentEventBase = { ...DocumentEditionEventBase, ...DocumentViewingEventBase };
export type DocumentEventBase = typeof DocumentEventBase;

export enum PermalinkEditionEvent {
  CONTENT_PERMALINKS_REVOKED = 'CONTENT_PERMALINKS_REVOKED',
  PERMALINK_CREATED = 'PERMALINK_CREATED',
  PERMALINK_UPDATED = 'PERMALINK_UPDATED',
  // EXPIRED_DOCUMENTS_PERMALINKS_REVOKED = "EXPIRED_DOCUMENTS_PERMALINKS_REVOKED",
  // DOCUMENT_PERMALINKS_REVOKED = 'DOCUMENT_PERMALINKS_REVOKED',
  // PERMALINK_REVOKED = 'PERMALINK_REVOKED',
  // PERMALINK_REVOKED_AFTER_EXPIRATION = 'PERMALINK_REVOKED_AFTER_EXPIRATION',
}

export enum PermalinkViewingEvent {
  PERMALINK_NOTIFIED = 'PERMALINK_NOTIFIED',
  PERMALINK_OPENED = 'PERMALINK_OPENED',
  PERMALINK_RETRIEVED = 'PERMALINK_RETRIEVED',
}

export const PermalinkEvent = { ...PermalinkEditionEvent, ...PermalinkViewingEvent };
export type PermalinkEvent = typeof PermalinkEvent;

export type PermalinkAudit = {
  type: string;
  version: string;
  documentation: string;
  userId: string;
  clientId: string;
  occurredOn: string;
  payload: any;
};

export type PermalinkAuditPage = {
  audits: PermalinkAudit[];
} & Page;

export const DocumentEditionEvent = { ...DocumentEditionEventBase, ...PermalinkEditionEvent };
export type DocumentEditionEvent = DocumentEditionEventBase & PermalinkEditionEvent;
export const DocumentViewingEvent = { ...DocumentViewingEventBase, ...PermalinkViewingEvent };
export type DocumentViewingEvent = DocumentViewingEventBase & PermalinkViewingEvent;
export const DocumentEvent = { ...DocumentEventBase, ...PermalinkEvent };
export type DocumentEvent = DocumentEventBase & PermalinkEvent;

export type Audit = (
  | {
      type: DocumentEditionEventBase.DOCUMENT_CREATED;
      payload: DocumentVersion<Object>;
    }
  | {
      type: DocumentEditionEventBase.DOCUMENT_VERSIONED;
      payload: {
        newDocument: DocumentVersion<Object>;
        originalDocument: DocumentVersion<Object>;
      };
    }
  | {
      type: DocumentEditionEventBase.DOCUMENT_TREE_DELETED | DocumentViewingEventBase.DOCUMENT_RETRIEVED;
      payload: DocumentVersionIdentifier;
    }
  | {
      type: DocumentViewingEventBase.CONTENTS_LISTED;
      payload: DocumentVersionIdentifier & {
        pageNumber: number;
        pageSize: number;
      };
    }
  | {
      type: DocumentViewingEventBase.CONTENT_VIEWED;
      payload: DocumentVersionIdentifier & { mimeType: string };
    }
  | {
      type: DocumentEditionEventBase.CONTENT_CREATED;
      payload: ContentCreated;
    }
  | {
      type: 'CONTENT_UPLOADED';
      payload: ContentUploaded;
    }
  | {
      type: 'CONTENT_DELETED';
      payload: ContentDeleted;
    }
  | {
      type: 'CONTENT_OCR_REQUESTED';
      payload: ContentOCRRequested;
    }
  | {
      type: 'CONTENT_OCR_VIEWED';
      payload: ContentOCRViewed;
    }
  | {
      type: 'RIGHT_APPLIED';
      payload: { rightId: string } & DocumentVersionIdentifier;
    }
) &
  AuditBase;

export type AuditPage = {
  audits: Audit[];
} & Page;
